// Reading a bank statement download (Liam 2026-09-27: tick off bank
// transfers; plan in docs/plans/bank-statement-matching.md). UK banks each
// lay their download out differently: one signed "Amount" column (Barclays,
// NatWest, HSBC, Starling), separate "Paid in" / "Paid out" or "Credit" /
// "Debit" columns (Lloyds, Halifax, TSB, Nationwide, Tide, Metro), a few
// lines of account details above the headings (Nationwide), or no headings
// at all (HSBC). This finds the date, the money and the words on each line,
// and keeps only money IN. Pure: no screen, no network, so it can be tested
// against each bank's layout.

export interface BankPayment {
  // yyyy-mm-dd
  date: string
  pence: number
  // Every word the bank gave about the payment, as written, for the screen.
  text: string
  // The payer's name when the bank has a column for it (Starling "Counter
  // Party", Monzo "Name", Revolut "Payer"), else null.
  who: string | null
  // Only the words the BANK wrote about the payment (name, reference,
  // description): never a type, category or the owner's own notes, which can
  // change between two downloads of the same payment. Used for matching and
  // for the payment's fingerprint.
  words: string
  // The bank's own id for the transaction, when the file has one (Monzo,
  // Tide, Revolut): the steadiest fingerprint there is.
  txid: string | null
  // The line's place in the file, from 1, for "row 12" in messages.
  line: number
}

export interface StatementRead {
  payments: BankPayment[]
  // Lines of money going out, or with no readable date or amount.
  ignored: number
  // What was found, for the screen: "Date, Amount, Description".
  columns: string[]
  problem: string | null
}

type Role = 'date' | 'amount' | 'in' | 'out' | 'who' | 'ref' | 'desc' | 'type' | 'balance' | 'txid' | 'state' | 'currency' | 'skip'

// Headings are compared lower case with "(£)", "(GBP)", "£" and "GBP" taken
// out, so "Credit (£)" and "Amount (GBP)" read as "credit" and "amount".
const HEADINGS: [Role, RegExp][] = [
  ['balance', /balance/],
  ['skip', /^(notes?|notes and #tags|category|category split|emoji|local amount|local currency|orig amount|orig currency|fee|receipt|address|time|card number|spending category)$/],
  ['date', /^(transaction date|date completed|completed date|posting date|posted date|value date|date posted|date|trans date|date started)/],
  ['in', /^(paid in|money in|credit amount|credits?|in|amount in|deposits?)$/],
  ['out', /^(paid out|money out|debit amount|debits?|out|amount out|withdrawals?)$/],
  ['amount', /^(amount|value|net amount|transaction amount)$/],
  ['who', /^(counter ?party|name|payer|payee|from|counterparty name|merchant)$/],
  ['ref', /^(reference|ref|memo|payment reference)$/],
  ['desc', /^(description|transaction description|details|narrative|particulars|transaction details|transaction)$/],
  ['type', /^(type|transaction type|subcategory)$/],
  ['txid', /^(transaction id|id|transaction number)$/],
  ['state', /^(state|status)$/],
  ['currency', /^(currency|payment currency)$/],
]

function roleOf(heading: string): Role | null {
  const h = heading.toLowerCase().replace(/\((£|gbp)\)|£|\bgbp\b/g, ' ').replace(/[():]/g, ' ').replace(/\s+/g, ' ').trim()
  if (!h) return null
  for (const [role, re] of HEADINGS) if (re.test(h)) return role
  return null
}

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 }

// UK order (day first). yyyy-mm-dd, dd/mm/yyyy, dd/mm/yy, dd-mm-yyyy,
// dd.mm.yyyy, "26 Sep 2026", "26-Sep-26", "26 September 2026", with or
// without a time after it.
export function parseBankDate(raw: string): string | null {
  const t = raw.trim()
  let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  let y: number, mo: number, d: number
  if (m) {
    y = Number(m[1]); mo = Number(m[2]); d = Number(m[3])
  } else if ((m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})\b/))) {
    d = Number(m[1]); mo = Number(m[2]); y = Number(m[3])
  } else if ((m = t.match(/^(\d{1,2})[\s-]([A-Za-z]{3,9})[\s-,]+(\d{2,4})\b/))) {
    d = Number(m[1]); mo = MONTHS[m[2].toLowerCase().slice(0, m[2].toLowerCase().startsWith('sept') ? 4 : 3)] ?? 0; y = Number(m[3])
  } else {
    return null
  }
  if (y < 100) y += 2000
  if (!mo || mo > 12 || d < 1 || d > 31 || y < 2000 || y > 2100) return null
  const iso = `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`
  const check = new Date(`${iso}T12:00:00Z`)
  return check.getUTCDate() === d ? iso : null
}

// "£1,234.56", "-12.00", "(12.00)", "12.00 CR", "12.00 DR", "+20". Pence,
// signed; null when it is not money.
export function parseBankMoney(raw: string): number | null {
  let t = raw.trim().replace(/[£\s,]/g, '').replace(/GBP/i, '')
  if (!t) return null
  let sign = 1
  if (/^\(.*\)$/.test(t)) { sign = -1; t = t.slice(1, -1) }
  if (/CR$/i.test(t)) t = t.slice(0, -2)
  else if (/DR$/i.test(t)) { sign = -sign; t = t.slice(0, -2) }
  if (t.startsWith('-')) { sign = -sign; t = t.slice(1) }
  else if (t.startsWith('+')) t = t.slice(1)
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null
  const [whole, frac = ''] = t.split('.')
  return sign * (Number(whole) * 100 + Number(frac.padEnd(2, '0')))
}

const looksDate = (c: string): boolean => parseBankDate(c) !== null
const looksMoney = (c: string): boolean => /\d/.test(c) && parseBankMoney(c) !== null

// The headings row: the first of the top 15 lines with a date heading and a
// money heading (so Nationwide's account-details lines above are skipped).
function findHeadings(rows: string[][]): { at: number; roles: (Role | null)[] } | null {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const roles = rows[i].map(roleOf)
    const hasDate = roles.includes('date')
    const hasMoney = roles.includes('amount') || roles.includes('in')
    // A heading row holds words, not a date and an amount of its own.
    const dataLike = rows[i].some(looksDate) && rows[i].some(looksMoney)
    if (hasDate && hasMoney && !dataLike) {
      // The first date column only (Revolut has "Date started" and "Date completed": keep the first found).
      let seenDate = false
      const first = roles.map((r) => {
        if (r !== 'date') return r
        if (seenDate) return 'skip' as Role
        seenDate = true
        return r
      })
      return { at: i, roles: first }
    }
  }
  return null
}

// No headings (HSBC): the date column, the one money column, the words.
function guessHeadless(rows: string[][]): (Role | null)[] | 'unsigned' | null {
  const sample = rows.slice(0, 30)
  const width = Math.max(...sample.map((r) => r.length))
  const share = (c: number, test: (s: string) => boolean): number => {
    const vals = sample.map((r) => r[c] ?? '').filter((v) => v.trim())
    return vals.length ? vals.filter(test).length / vals.length : 0
  }
  const roles: (Role | null)[] = Array(width).fill(null)
  const dateCol = [...Array(width).keys()].find((c) => share(c, looksDate) >= 0.8)
  if (dateCol === undefined) return null
  roles[dateCol] = 'date'
  const moneyCols = [...Array(width).keys()].filter((c) => c !== dateCol && share(c, looksMoney) >= 0.8)
  if (moneyCols.length === 0) return null
  // With a balance column last, the amount is the one before it. It must
  // carry signs (money out as minus): unsigned columns cannot say which way
  // the money went, so such a file is refused rather than guessed.
  // Every row, not the first 30: a newest-first file can open with a month of payments in (round 2, finding 6).
  const signed = rows.some((r) => (parseBankMoney(r[moneyCols[0]] ?? '') ?? 0) < 0)
  if (!signed) return 'unsigned'
  roles[moneyCols[0]] = 'amount'
  for (const c of moneyCols.slice(1)) roles[c] = 'balance'
  for (let c = 0; c < width; c++) if (roles[c] === null && share(c, (s) => /[A-Za-z]/.test(s)) >= 0.5) roles[c] = 'desc'
  return roles
}

const LABEL: Record<Role, string> = { date: 'Date', amount: 'Amount', in: 'Paid in', out: 'Paid out', who: 'Name', ref: 'Reference', desc: 'Description', type: 'Type', balance: 'Balance', txid: '', state: '', currency: '', skip: '' }
const CANT_TELL = "Roundly couldn't tell money in from money out in this file. Download a few weeks that include money going out, as CSV."
const IN_WORDS = /^(credit|cr|in|money in|paid in|deposit|receipt)$/i
const OUT_WORDS = /^(debit|dr|out|money out|paid out|withdrawal|payment)$/i

export function readStatement(rows: string[][]): StatementRead {
  const found = findHeadings(rows)
  let roles: (Role | null)[] | 'unsigned' | null
  let body: string[][]
  let firstLine: number
  if (found) {
    roles = found.roles
    body = rows.slice(found.at + 1)
    firstLine = found.at + 2
  } else {
    roles = guessHeadless(rows)
    body = rows
    firstLine = 1
  }
  if (roles === 'unsigned') return { payments: [], ignored: rows.length, columns: [], problem: CANT_TELL }
  if (!roles) return { payments: [], ignored: rows.length, columns: [], problem: "Roundly couldn't find the date and the money in on this file." }
  const col = (r: Role): number[] => roles!.map((x, i) => (x === r ? i : -1)).filter((i) => i >= 0)
  const [dateCol] = col('date')
  const amountCol = col('amount')[0]
  const inCol = col('in')[0]
  const outCol = col('out')[0]
  const whoCols = col('who')
  const bankWordCols = [...whoCols, ...col('ref'), ...col('desc')]
  const typeCols = col('type')
  const [txidCol] = col('txid')
  const [stateCol] = col('state')
  const [currencyCol] = col('currency')
  void outCol
  // One signed Amount column: money out must show as minus somewhere, or a
  // Type column must say which way each line went ("CREDIT" / "DEBIT").
  let signFromType: number | undefined
  if (inCol === undefined && amountCol !== undefined) {
    const anyMinus = body.some((r) => (parseBankMoney(r[amountCol] ?? '') ?? 0) < 0)
    if (!anyMinus) {
      signFromType = typeCols.find((c) => body.some((r) => IN_WORDS.test((r[c] ?? '').trim())) && body.some((r) => OUT_WORDS.test((r[c] ?? '').trim())))
      if (signFromType === undefined) return { payments: [], ignored: body.length, columns: [], problem: CANT_TELL }
    }
  }
  if (inCol === undefined && amountCol === undefined) return { payments: [], ignored: body.length, columns: [], problem: CANT_TELL }
  const payments: BankPayment[] = []
  let ignored = 0
  body.forEach((r, i) => {
    const date = parseBankDate(r[dateCol] ?? '')
    let pence: number | null = null
    if (inCol !== undefined) {
      // A Paid in column: an empty one on a money-out line leaves it out.
      const v = parseBankMoney(r[inCol] ?? '')
      pence = v !== null ? Math.abs(v) : null
    } else if (amountCol !== undefined) {
      pence = parseBankMoney(r[amountCol] ?? '')
      if (pence !== null && signFromType !== undefined) {
        const t = (r[signFromType] ?? '').trim()
        pence = IN_WORDS.test(t) ? Math.abs(pence) : OUT_WORDS.test(t) ? -Math.abs(pence) : null
      }
    }
    // Revolut and others: only completed payments, only in pounds.
    const stateWord = stateCol !== undefined ? (r[stateCol] ?? '').trim().toUpperCase() : ''
    const currency = currencyCol !== undefined ? (r[currencyCol] ?? '').trim().toUpperCase() : ''
    // Only lines a bank says did not go through are dropped (round 2, finding 9).
    const notThrough = /PENDING|REVERT|DECLIN|FAIL|CANCEL|REJECT/.test(stateWord)
    if (!date || pence === null || pence <= 0 || notThrough || (currency && currency !== 'GBP')) {
      ignored++
      return
    }
    const collect = (cols: number[]): string[] => {
      const out: string[] = []
      for (const c of cols) {
        const w = (r[c] ?? '').trim()
        if (w && !out.includes(w)) out.push(w)
      }
      return out
    }
    const bankWords = collect(bankWordCols)
    const shown = [...bankWords, ...collect(typeCols).filter((t) => !bankWords.includes(t))]
    const who = whoCols.map((c) => (r[c] ?? '').trim()).find(Boolean) ?? null
    const txid = txidCol !== undefined ? (r[txidCol] ?? '').trim() || null : null
    payments.push({ date, pence, text: shown.join(' · '), who, words: bankWords.join(' '), txid, line: firstLine + i })
  })
  const columns = roles.map((x) => (x ? LABEL[x] : '')).filter(Boolean)
  const problem = payments.length === 0 ? 'No money coming in was found in this file.' : null
  return { payments, ignored, columns: [...new Set(columns)], problem }
}
